"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Shaka = void 0;
const lodash_merge_1 = __importDefault(require("lodash.merge"));
const shaka_player_1 = __importDefault(require("shaka-player"));
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const AbstractBaseEngine_1 = require("./AbstractBaseEngine");
const defaultConfigurations_1 = require("./defaultConfigurations");
const interfaces_1 = require("./interfaces");
const restrictions_1 = require("./utils/restrictions");
const ShakaErrors_1 = require("./utils/ShakaErrors");
// Comment out the below code to enable debugging, remember to remove the default shaka import.
// import shaka from "shaka-player/dist/shaka-player.compiled.debug";
// shaka.log.setLevel(shaka.log.Level.V2);
const DECODER_ERROR_MESSAGE = "DECODER_ERROR_NOT_SUPPORTED";
function createTrack(track) {
    var _a;
    let kind;
    const label = track.label || (0, js_player_shared_1.getLabel)(track.language);
    if (track.type === "variant") {
        kind = (0, AbstractBaseEngine_1.getAudioKind)((_a = track.audioRoles) !== null && _a !== void 0 ? _a : []);
    }
    else if (track.type === "text") {
        kind = (0, AbstractBaseEngine_1.getTextKind)(track.roles);
    }
    return {
        id: `${track.language} - ${label}`,
        language: track.language,
        kind,
        label,
        raw: track,
    };
}
const HEVC_REGEX = new RegExp(/hev1|hvc1/);
/**
 * monkey patch MediaSource.isTypeSupported to disable HEVC support
 */
let hevcDisabled = false;
function disableHEVC() {
    hevcDisabled = true;
    const isTypeSupported = MediaSource.isTypeSupported;
    MediaSource.isTypeSupported = (mimeType) => {
        if (mimeType && HEVC_REGEX.test(mimeType)) {
            return false;
        }
        return isTypeSupported(mimeType);
    };
}
const MAX_RECOVERY_ATTEMPS = 1;
class Shaka extends AbstractBaseEngine_1.AbstractBaseEngine {
    constructor(videoElement, instanceSettings) {
        super(videoElement, instanceSettings);
        this.name = "Shaka Player";
        this.playertechVersion = shaka_player_1.default.Player.version;
        this.utcTimeIsSet = false;
        this.recoveryAttempts = 0;
        shaka_player_1.default.polyfill.installAll();
        this.shakaPlayer = new shaka_player_1.default.Player();
        this.shakaPlayer.attach(videoElement);
        this.instanceSettings = instanceSettings;
        this.subtitle = { language: undefined, kind: undefined };
        this.audio = { language: undefined, kind: undefined };
        this.setupEventListeners();
    }
    setupEventListeners() {
        super.setupEventListeners();
        const networkEngine = this.shakaPlayer.getNetworkingEngine();
        networkEngine === null || networkEngine === void 0 ? void 0 : networkEngine.registerRequestFilter((type) => {
            var _a;
            if (type === shaka_player_1.default.net.NetworkingEngine.RequestType.LICENSE) {
                switch ((_a = this.shakaPlayer.drmInfo()) === null || _a === void 0 ? void 0 : _a.keySystem) {
                    case js_player_shared_1.KeySystem.PLAYREADY:
                    case js_player_shared_1.KeySystem.PLAYREADY_CHROMECAST:
                        this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "PLAYREADY_LICENSE_REQUEST");
                        break;
                    case js_player_shared_1.KeySystem.WIDEVINE:
                        this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "WIDEVINE_LICENSE_REQUEST");
                        break;
                    case js_player_shared_1.KeySystem.FAIRPLAY:
                        this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "FAIRPLAY_LICENSE_REQUEST");
                        break;
                }
            }
        });
        networkEngine === null || networkEngine === void 0 ? void 0 : networkEngine.registerResponseFilter((type) => {
            var _a;
            if (type === shaka_player_1.default.net.NetworkingEngine.RequestType.LICENSE) {
                switch ((_a = this.shakaPlayer.drmInfo()) === null || _a === void 0 ? void 0 : _a.keySystem) {
                    case js_player_shared_1.KeySystem.PLAYREADY:
                    case js_player_shared_1.KeySystem.PLAYREADY_CHROMECAST:
                        this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "PLAYREADY_LICENSE_RESPONSE");
                        break;
                    case js_player_shared_1.KeySystem.WIDEVINE:
                        this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "WIDEVINE_LICENSE_RESPONSE");
                        break;
                    case js_player_shared_1.KeySystem.FAIRPLAY:
                        this.emit(interfaces_1.EngineEvents.DRM_UPDATE, "FAIRPLAY_LICENSE_RESPONSE");
                        break;
                }
            }
        });
        this.shakaPlayer.addEventListener("adaptation", () => {
            var _a, _b;
            const activeTracks = this.shakaPlayer
                .getVariantTracks()
                .filter((track) => track.active);
            if (!activeTracks.length) {
                return;
            }
            const bitrate = activeTracks.reduce((btr, track) => btr + track.bandwidth, 0);
            const videoTrack = activeTracks.find((track) => track.type === "variant");
            if (videoTrack) {
                this.emit(interfaces_1.EngineEvents.BITRATE_CHANGED, {
                    bitrate,
                    width: (_a = videoTrack.width) !== null && _a !== void 0 ? _a : -1,
                    height: (_b = videoTrack.height) !== null && _b !== void 0 ? _b : -1,
                });
            }
        });
        this.shakaPlayer.addEventListener("error", ((event) => {
            this.handleError(event.detail);
        }));
        this.shakaPlayer.addEventListener("trackschanged", this.onTracksChange.bind(this));
        this.shakaPlayer.addEventListener("timelineregionenter", ((evt) => {
            var _a;
            const schemeIdURIs = (_a = this.instanceSettings.initOptions) === null || _a === void 0 ? void 0 : _a.metadataURIs;
            const metadataEvent = {
                event: [],
                engineName: "Shaka Player",
                engineVersion: shaka_player_1.default.Player.version,
            };
            if (schemeIdURIs === null || schemeIdURIs === void 0 ? void 0 : schemeIdURIs.length) {
                schemeIdURIs.forEach((uri) => {
                    if (uri === evt.detail.schemeIdUri) {
                        metadataEvent.event = evt;
                        this.emit(interfaces_1.EngineEvents.METADATA_EVENT, metadataEvent);
                    }
                });
            }
            else if (schemeIdURIs) {
                metadataEvent.event = evt;
                this.emit(interfaces_1.EngineEvents.METADATA_EVENT, metadataEvent);
            }
        }));
    }
    setupStartGapDetection() {
        // workaround for shaka not detecting gaps in the beginning
        const onProgress = () => {
            if (this.getPlaybackState() !== js_player_shared_1.PlaybackState.LOADING) {
                return;
            }
            const buffered = this.videoElement.buffered;
            if (buffered.length) {
                const start = buffered.start(0);
                const end = buffered.end(0);
                if (start > 0 && end - start > 0) {
                    this.removeVideoEventListener("progress", onProgress);
                    // we have something buffered and there is a gap in the beginning, seek to start of buffer to avoid getting stuck
                    this.videoElement.currentTime = start;
                }
            }
        };
        this.addVideoEventListener("progress", onProgress);
    }
    convertToUTCTime(time) {
        const presentationStartTime = this.shakaPlayer.getPresentationStartTimeAsDate();
        if (this.isLive() && presentationStartTime) {
            time += presentationStartTime.getTime() / 1000;
            return time * 1000;
        }
        if (this.utcTimeIsSet) {
            return super.convertToUTCTime(time);
        }
        return -1;
    }
    _load({ src, license, startTime, audio, subtitle, }) {
        const _super = Object.create(null, {
            load: { get: () => super.load }
        });
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            this.startTime = startTime;
            const customConfiguration = this.instanceSettings.initOptions.customShakaConfiguration || {};
            const configuration = (0, lodash_merge_1.default)({}, defaultConfigurations_1.Shaka, customConfiguration);
            if ((_a = this.instanceSettings.mediaInfo) === null || _a === void 0 ? void 0 : _a.isLive) {
                (0, lodash_merge_1.default)(configuration, defaultConfigurations_1.ShakaLive);
            }
            this.src = src;
            this.setLicense(this.instanceSettings.supportedKeySystem, license);
            this.shakaPlayer.configure(configuration);
            this.shakaPlayer.configure("manifest.dash.manifestPreprocessor", (0, js_player_shared_1.patchShakaSSAIManifest)((_c = (_b = this.instanceSettings.playResponse) === null || _b === void 0 ? void 0 : _b.ads) === null || _c === void 0 ? void 0 : _c.clips));
            this.setResolutionAndBitrateRestrictions(yield (0, restrictions_1.getResolutionAndBitrateRestrictions)((_d = this.instanceSettings.playResponse) === null || _d === void 0 ? void 0 : _d.contractRestrictions));
            this.setContentSpecificConfiguration();
            this.setContextSpecificConfiguration();
            this.setPlaybackState(js_player_shared_1.PlaybackState.LOADING);
            if (audio === null || audio === void 0 ? void 0 : audio.language) {
                this.audio = {
                    language: audio.language,
                    kind: audio.kind,
                };
                this.shakaPlayer.configure("preferredAudioLanguage", audio.language);
                if ((_e = this.audio) === null || _e === void 0 ? void 0 : _e.kind) {
                    this.shakaPlayer.configure("preferredVariantRole", (0, AbstractBaseEngine_1.getDashAudioKind)(this.audio.kind));
                }
            }
            if (subtitle === null || subtitle === void 0 ? void 0 : subtitle.language) {
                this.subtitle = {
                    language: subtitle.language,
                    kind: subtitle.kind,
                };
                this.shakaPlayer.configure("preferredTextLanguage", subtitle.language);
                if ((_f = this.subtitle) === null || _f === void 0 ? void 0 : _f.kind) {
                    this.shakaPlayer.configure("preferredTextRole", (0, AbstractBaseEngine_1.getDashTextKind)(this.subtitle.kind));
                }
            }
            if (startTime === undefined || startTime === 0) {
                this.setupStartGapDetection();
            }
            this.shakaPlayer
                .load(src, startTime)
                .then(() => {
                _super.load.call(this, { src, license, startTime });
            })
                .catch((err) => {
                this.handleError(err);
            });
        });
    }
    load(parameters) {
        // load doesn't return a promise but since we want to use async/await we call a private method
        this._load(parameters);
    }
    setPreferredLanguages() {
        // no-op, done in load
    }
    stop() {
        if (this.shakaPlayer.getLoadMode() !== shaka_player_1.default.Player.LoadMode.DESTROYED) {
            this.shakaPlayer.unload();
        }
        super.stop();
    }
    isLive() {
        return this.shakaPlayer.isLive();
    }
    addSubtitleEvents() {
        this.shakaPlayer.addEventListener("textchanged", () => {
            return this.onTextTracksChange(this.shouldUpdatePreferences);
        });
    }
    addAudioTrackEvents() {
        this.shakaPlayer.addEventListener("variantchanged", this.onAudioTrackChange.bind(this));
    }
    getSeekable() {
        const { start, end } = this.shakaPlayer.seekRange();
        return { start, end };
    }
    getUTCSeekable() {
        const seekable = this.shakaPlayer.seekRange();
        return {
            start: this.convertToUTCTime(seekable.start),
            end: this.convertToUTCTime(seekable.end),
        };
    }
    setLicense(supportedKeySystem, license) {
        if (license) {
            if (supportedKeySystem === js_player_shared_1.KeySystem.WIDEVINE) {
                this.setWidevine(license);
            }
            else if (supportedKeySystem === js_player_shared_1.KeySystem.PLAYREADY) {
                this.setPlayready(license);
            }
        }
    }
    setPlayready(license) {
        const playreadyLicenseObject = license[js_player_shared_1.KeySystem.PLAYREADY];
        if (playreadyLicenseObject) {
            this.shakaPlayer.configure({
                drm: {
                    servers: {
                        [js_player_shared_1.KeySystem.PLAYREADY]: playreadyLicenseObject.licenseServerUrl,
                    },
                },
            });
        }
    }
    setWidevine(license) {
        const widevineLicenseObject = license[js_player_shared_1.KeySystem.WIDEVINE];
        if (widevineLicenseObject) {
            this.shakaPlayer.configure({
                drm: {
                    servers: {
                        [js_player_shared_1.KeySystem.WIDEVINE]: widevineLicenseObject.licenseServerUrl,
                    },
                    advanced: {
                        [js_player_shared_1.KeySystem.WIDEVINE]: {
                            videoRobustness: "SW_SECURE_CRYPTO",
                            audioRobustness: "SW_SECURE_CRYPTO",
                        },
                    },
                },
            });
        }
    }
    setResolutionAndBitrateRestrictions({ minBitrate, maxBitrate, maxResHeight, }) {
        if (!minBitrate && !maxBitrate && !maxResHeight)
            return;
        this.shakaPlayer.configure({
            restrictions: Object.assign(Object.assign(Object.assign({}, (minBitrate && { minBandwidth: minBitrate })), (maxBitrate && { maxBandwidth: maxBitrate })), (maxResHeight && { maxHeight: maxResHeight })),
        });
    }
    setContentSpecificConfiguration() {
        if (!this.instanceSettings.mediaInfo || !this.instanceSettings.formats)
            return;
        const isLive = this.instanceSettings.mediaInfo.isLive;
        if (isLive) {
            const currentPlaybackObject = this.instanceSettings.formats.find((f) => f.mediaLocator === this.src);
            if (currentPlaybackObject === null || currentPlaybackObject === void 0 ? void 0 : currentPlaybackObject.liveDelay) {
                this.shakaPlayer.configure({
                    manifest: {
                        defaultPresentationDelay: currentPlaybackObject.liveDelay / 1000,
                    },
                });
            }
        }
    }
    setContextSpecificConfiguration() {
        if (!window.ResizeObserver)
            return;
        this.resizeObserver = new ResizeObserver((0, js_player_shared_1.debounce)(this.capLevelToPlayerSize.bind(this)));
        this.resizeObserver.observe(this.videoElement);
    }
    capLevelToPlayerSize() {
        var _a, _b, _c, _d, _e, _f;
        const caps = [
            {
                min: 0,
                max: 1280,
                value: 1280,
            },
            {
                min: 1281,
                max: 1920,
                value: 1920,
            },
        ];
        if (!this.shakaPlayer ||
            this.shakaPlayer.getLoadMode() === shaka_player_1.default.Player.LoadMode.DESTROYED) {
            return;
        }
        const heightRestriction = (_b = (_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.contractRestrictions) === null || _b === void 0 ? void 0 : _b.maxResHeight;
        const maxWidthSet = (_e = (_d = (_c = this.shakaPlayer.getConfiguration()) === null || _c === void 0 ? void 0 : _c.abr) === null || _d === void 0 ? void 0 : _d.restrictions) === null || _e === void 0 ? void 0 : _e.maxWidth;
        const videoElementWidth = this.videoElement.clientWidth;
        let maxWidthToSet = ((_f = caps.find((cap) => cap.min <= videoElementWidth && cap.max >= videoElementWidth)) === null || _f === void 0 ? void 0 : _f.value) || Infinity;
        if (maxWidthToSet === maxWidthSet)
            return;
        if (heightRestriction) {
            const levels = this.getQualityLevels().sort((a, b) => b.width - a.width);
            const restrictedMaxLevel = levels.find((level) => level.width <= maxWidthToSet && level.height <= heightRestriction);
            if (restrictedMaxLevel) {
                maxWidthToSet = restrictedMaxLevel.width;
            }
        }
        this.shakaPlayer.configure("abr.restrictions.maxWidth", maxWidthToSet);
    }
    setQualityLevel(level) {
        const track = this.shakaPlayer
            .getVariantTracks()
            .find((track) => track.id === level.id);
        if (track) {
            this.shakaPlayer.configure("abr.enabled", false);
            this.shakaPlayer.selectVariantTrack(track, true, 10);
            /**
             * Shaka won't trigger adaption change events when manually setting the level, and disabling the abr engine
             * https://shaka-player-demo.appspot.com/docs/api/shaka.Player.html#.event:AdaptationEvent
             * Therefore we trigger a manual one.
             */
            this.emit(interfaces_1.EngineEvents.BITRATE_CHANGED, {
                bitrate: level.bandwidth,
                width: level.width,
                height: level.height,
            });
        }
        else {
            this.shakaPlayer.configure("abr.enabled", true);
        }
    }
    getQualityLevel() {
        var _a, _b, _c;
        const activeTrack = this.shakaPlayer
            .getVariantTracks()
            .find((track) => track.active);
        if (!activeTrack) {
            return super.getQualityLevel();
        }
        return {
            id: activeTrack.id,
            bandwidth: activeTrack.bandwidth,
            width: (_a = activeTrack.width) !== null && _a !== void 0 ? _a : -1,
            height: (_b = activeTrack.height) !== null && _b !== void 0 ? _b : -1,
            framerate: (_c = activeTrack.frameRate) !== null && _c !== void 0 ? _c : undefined,
        };
    }
    getQualityLevels() {
        var _a;
        if ((_a = this.instanceSettings.loadOptions) === null || _a === void 0 ? void 0 : _a.audioOnly)
            return [];
        if (!this.shakaPlayer.getVariantTracks() ||
            !Array.isArray(this.shakaPlayer.getVariantTracks()))
            return [];
        const activeTrack = this.shakaPlayer
            .getVariantTracks()
            .find((track) => track.active);
        const qualityLevels = this.shakaPlayer
            .getVariantTracks()
            .filter((track) => track.audioId === (activeTrack === null || activeTrack === void 0 ? void 0 : activeTrack.audioId) &&
            track.audioBandwidth === (activeTrack === null || activeTrack === void 0 ? void 0 : activeTrack.audioBandwidth))
            .map((track) => {
            var _a, _b, _c;
            const qualityLevel = {
                id: track.id,
                bandwidth: track.bandwidth,
                width: (_a = track.width) !== null && _a !== void 0 ? _a : -1,
                height: (_b = track.height) !== null && _b !== void 0 ? _b : -1,
                framerate: (_c = track.frameRate) !== null && _c !== void 0 ? _c : undefined,
            };
            qualityLevel.name = (0, AbstractBaseEngine_1.getQualityLevelName)(qualityLevel);
            return qualityLevel;
        });
        return [...super.getQualityLevels(), ...qualityLevels];
    }
    setInternalSubtitle(subtitle) {
        this.subtitle = subtitle;
    }
    setInternalAudio(audio) {
        this.audio.language = audio.language;
        this.audio.kind = audio.kind;
    }
    toggleSubtitles(enabled) {
        // Shaka reuses the same text track for all languages and it sets the "mode" according to this behavior:
        // * "showing": if the visibility is true (`setTextTrackVisibility(true)`)
        // * "hidden": if the visibility is false but alwaysStreamText is true
        // * "disabled": if both are false
        //
        // We want to switch between fully disabled and hidden (text track and cue events enabled for our custom rendering).
        // So visibility should always be false and alwaysStreamText must be true when subs are enabled
        this.shakaPlayer.configure("streaming.alwaysStreamText", enabled);
        this.shakaPlayer.setTextTrackVisibility(false);
    }
    setSubtitleTrack(track, shouldUpdatePreferences) {
        const internalTrack = this.shakaPlayer
            .getTextTracks()
            .find((t) => { var _a; return track && ((_a = createTrack(t)) === null || _a === void 0 ? void 0 : _a.id) === track.id; });
        this.toggleSubtitles(!!internalTrack);
        if (track) {
            if (!internalTrack) {
                // TODO: inform the consumer that the selected track could not be found?
                return;
            }
            this.setInternalSubtitle({
                language: internalTrack.language,
                kind: internalTrack.roles && (0, AbstractBaseEngine_1.getTextKind)(internalTrack.roles),
            });
            if (!internalTrack.active) {
                this.shouldUpdatePreferences = shouldUpdatePreferences;
                this.shakaPlayer.selectTextTrack(internalTrack);
            }
            else {
                this.onTextTracksChange(this.shouldUpdatePreferences);
            }
        }
        else {
            const forcedSubtitle = this.shakaPlayer
                .getTextTracks()
                .find((track) => track.roles.includes("forced-subtitle"));
            if (forcedSubtitle) {
                this.setInternalSubtitle({
                    language: forcedSubtitle.language,
                    kind: forcedSubtitle.roles && (0, AbstractBaseEngine_1.getTextKind)(forcedSubtitle.roles),
                });
                if (!forcedSubtitle.active) {
                    this.shakaPlayer.selectTextTrack(forcedSubtitle);
                }
            }
            else {
                // shaka can't disable text tracks, we set an internal variable to see if text tracks
                // are enabled
                this.setInternalSubtitle({
                    language: undefined,
                    kind: undefined,
                });
                this.onTextTracksChange();
            }
        }
    }
    getSubtitleTrack() {
        const shakaTrack = this.shakaPlayer
            .getTextTracks()
            .find((track) => track.active);
        if (!shakaTrack) {
            return undefined;
        }
        const track = createTrack(shakaTrack);
        if (this.subtitle.language === track.language &&
            this.subtitle.kind === track.kind) {
            return track;
        }
        if (this.subtitle.kind === "forced") {
            const shakaForcedTrack = this.shakaPlayer
                .getTextTracks()
                .find((track) => track.roles.includes("forced-subtitle"));
            const forcedTrack = !!shakaForcedTrack && createTrack(shakaForcedTrack);
            return forcedTrack || undefined;
        }
        return undefined;
    }
    getSubtitleTracks() {
        return this.filterVisibleSubtitleTracks(this.shakaPlayer
            .getTextTracks()
            .map((track) => createTrack(track))
            .filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.label === compTrack.label &&
            track.kind === compTrack.kind) === index));
    }
    getSubtitleTracksWithForced() {
        return this.shakaPlayer
            .getTextTracks()
            .map((track) => createTrack(track))
            .filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.label === compTrack.label &&
            track.kind === compTrack.kind) === index);
    }
    setAudioTrack(track) {
        var _a;
        if ((_a = track.raw) === null || _a === void 0 ? void 0 : _a.label) {
            this.shakaPlayer.selectVariantsByLabel(track.raw.label);
        }
        else {
            // Changing audio tracks while abr manager is enabled will likely result in the selected track being overridden.
            // To not cause it to be overridden we disabling abr before switching audio tracks.
            this.shakaPlayer.configure("abr.enabled", false);
            this.shakaPlayer.selectAudioLanguage(track.language, (0, AbstractBaseEngine_1.getDashAudioKind)(track.kind));
            this.shakaPlayer.configure("abr.enabled", true);
        }
        this.setInternalAudio(track);
        this.onAudioTrackChange();
    }
    getAudioTrack() {
        const track = this.shakaPlayer
            .getVariantTracks()
            .find((track) => track.active);
        if (track) {
            return createTrack(track);
        }
    }
    getAudioTracks() {
        return this.shakaPlayer
            .getVariantTracks()
            .map((track) => createTrack(track))
            .filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.label === compTrack.label &&
            track.kind === compTrack.kind) === index);
    }
    onTracksChange() {
        const forcedSubtitle = this.getSubtitleTracksWithForced().find((subtitle) => subtitle.kind === "forced");
        let track;
        if (this.subtitle.language) {
            // multi-period dash won't set the correct subtitle language
            // if subtitles aren't part of the first period.
            track = this.getSubtitleTracks().find((track) => track.language === this.subtitle.language &&
                (!this.subtitle.kind || this.subtitle.kind === track.kind));
            if (!track && !!forcedSubtitle) {
                this.setSubtitleTrack(forcedSubtitle, false);
            }
            if (track) {
                this.setSubtitleTrack(track, false);
            }
        }
        if (!this.subtitle.language && !!forcedSubtitle) {
            this.shouldUpdatePreferences = false;
            this.setSubtitleTrack(forcedSubtitle, false);
        }
        super.onTracksChange();
    }
    onError() {
        // Errors are handled by Shaka, ignore native errors
    }
    handleError(error) {
        var _a, _b, _c;
        const videoCodec = (_a = this.shakaPlayer
            .getVariantTracks()
            .find((variant) => variant.active)) === null || _a === void 0 ? void 0 : _a.videoCodec;
        if (videoCodec &&
            ((_c = (_b = error === null || error === void 0 ? void 0 : error.data) === null || _b === void 0 ? void 0 : _b[2]) === null || _c === void 0 ? void 0 : _c.includes(DECODER_ERROR_MESSAGE)) &&
            HEVC_REGEX.test(videoCodec) &&
            !hevcDisabled) {
            disableHEVC();
            this.shakaPlayer.unload().then(() => {
                if (this.src) {
                    return this.load({
                        src: this.src,
                        license: undefined,
                        startTime: this.startTime,
                        audio: this.audio,
                        subtitle: this.subtitle,
                    });
                }
            });
        }
        else {
            const { error: playerError, fatal } = (0, ShakaErrors_1.convertError)(error);
            // If playerError isn't fatal then we have an error that we might be able to recover from
            if (!fatal && MAX_RECOVERY_ATTEMPS > this.recoveryAttempts) {
                this.recoveryAttempts += 1;
                const retrying = this.shakaPlayer.retryStreaming();
                if (retrying)
                    return;
            }
            this.emit(interfaces_1.EngineEvents.ERROR, playerError);
        }
    }
    destroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.shakaPlayer.destroy();
        super.destroy();
    }
}
exports.Shaka = Shaka;
//# sourceMappingURL=Shaka.js.map